import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import CopyToClipboard from 'react-copy-to-clipboard';
import { makeStyles } from '@material-ui/core';

import p from 'theme/palette';

var useStyles = makeStyles(function() {
  return {
    container: {
      textAlign: 'center',
      padding: '4px 0 8px',
    },
    illustration: {
      width: 80,
      height: 80,
      margin: '0 auto 20px',
      position: 'relative',
    },
    blob: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%)',
      borderRadius: '60% 40% 50% 50% / 50% 60% 40% 50%',
      animation: '$blobPulse 4s ease-in-out infinite',
    },
    '@keyframes blobPulse': {
      '0%, 100%': {
        borderRadius: '60% 40% 50% 50% / 50% 60% 40% 50%',
      },
      '50%': {
        borderRadius: '50% 60% 40% 60% / 60% 40% 60% 40%',
      },
    },
    iconWrapper: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    icon: {
      fontSize: 36,
      color: p.brandOrange,
    },
    title: {
      fontFamily: 'Montserrat, sans-serif',
      fontSize: 17,
      fontWeight: 600,
      color: p.defaultText,
      marginBottom: 8,
    },
    message: {
      fontFamily: 'Montserrat, sans-serif',
      fontSize: 14,
      lineHeight: 1.6,
      color: p.grey600,
      marginBottom: 24,
      maxWidth: 300,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    errorCodeSection: {
      marginBottom: 24,
      padding: '16px 20px',
      backgroundColor: p.bgSurfaceRaised,
      borderRadius: 10,
      border: '1px solid ' + p.cardTitleBorder,
    },
    errorCodeLabel: {
      fontFamily: 'Montserrat, sans-serif',
      fontSize: 10,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      color: p.grey400,
      marginBottom: 8,
    },
    errorCodeBox: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 10,
      padding: '10px 14px',
      backgroundColor: p.bgSurface,
      border: '1px solid ' + p.border,
      borderRadius: 8,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: p.grey400,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      },
    },
    errorCode: {
      fontFamily: '"JetBrains Mono", "Fira Code", "Source Code Pro", "SF Mono", Consolas, monospace',
      fontSize: 13,
      fontWeight: 500,
      color: p.defaultText,
      letterSpacing: '0.02em',
    },
    copyIcon: {
      fontSize: 16,
      color: p.grey400,
      transition: 'all 0.2s ease',
    },
    copyIconCopied: {
      color: p.success,
    },
    copyHint: {
      fontFamily: 'Montserrat, sans-serif',
      fontSize: 11,
      color: p.grey400,
      marginTop: 8,
      transition: 'color 0.2s ease',
    },
    copyHintCopied: {
      color: p.success,
    },
    contactButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      fontFamily: 'Montserrat, sans-serif',
      fontSize: 14,
      fontWeight: 500,
      color: p.defaultText,
      padding: '10px 24px',
      minWidth: 100,
      borderRadius: 6,
      backgroundColor: p.bgSurface,
      border: '1px solid ' + p.border,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: p.bgSurfaceRaised,
        borderColor: p.grey400,
      },
    },
    contactIcon: {
      fontSize: 18,
    },
  };
});

export var ErrorDialogContent = function(props) {
  var t = props.t;
  var item = props.item;

  var classes = useStyles();
  var copiedState = useState(false);
  var copied = copiedState[0];
  var setCopied = copiedState[1];

  var hasErrorCode = item && item.uuid;
  var errorCode = hasErrorCode ? item.uuid : null;
  var showErrorCode = hasErrorCode;

  var handleCopy = useCallback(function() {
    setCopied(true);
    setTimeout(function() {
      setCopied(false);
    }, 2500);
  }, [setCopied]);

  return (
    <div className={classes.container}>
      <div className={classes.illustration}>
        <div className={classes.blob} />
        <div className={classes.iconWrapper}>
          <i className={'material-symbols-outlined ' + classes.icon}>sentiment_dissatisfied</i>
        </div>
      </div>

      <div className={classes.title}>
        {t('errorDialog:oops')}
      </div>

      <div className={classes.message}>
        {t('errorDialog:friendlyMessage')}
      </div>

      {showErrorCode && (
        <div className={classes.errorCodeSection}>
          <div className={classes.errorCodeLabel}>
            {t('errorDialog:referenceCode')}
          </div>
          <CopyToClipboard text={errorCode} onCopy={handleCopy}>
            <div className={classes.errorCodeBox}>
              <span className={classes.errorCode}>{errorCode}</span>
              <i className={
                'material-symbols-outlined ' + classes.copyIcon +
                (copied ? ' ' + classes.copyIconCopied : '')
              }>
                {copied ? 'check_circle' : 'content_copy'}
              </i>
            </div>
          </CopyToClipboard>
          <div className={
            classes.copyHint +
            (copied ? ' ' + classes.copyHintCopied : '')
          }>
            {copied ? t('errorDialog:copied') : t('errorDialog:clickToCopy')}
          </div>
        </div>
      )}

      <button
        type="button"
        className={classes.contactButton}
        onClick={function() { window.location.href = 'mailto:support@slatepages.com'; }}
      >
        <i className={'material-symbols-outlined ' + classes.contactIcon}>mail</i>
        {t('errorDialog:getHelp')}
      </button>
    </div>
  );
};

ErrorDialogContent.propTypes = {
  handleClose: PropTypes.func.isRequired,
  item: PropTypes.object,
  t: PropTypes.func.isRequired,
};

ErrorDialogContent.defaultProps = {
  item: {},
};
